import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { formatUserDisplayName, isPlaceholderLastName, readProfileExtras } from '../../types/userProfile';

function displayName(
  prefix?: string | null,
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string {
  return formatUserDisplayName({ prefix, firstName, lastName, email });
}

function initials(firstName?: string | null, lastName?: string | null, email?: string | null): string {
  const first = firstName?.trim()?.[0];
  const last = isPlaceholderLastName(lastName) ? undefined : lastName?.trim()?.[0];
  if (first && last) return `${first}${last}`.toUpperCase();
  if (first) return first.toUpperCase();
  return (email?.trim()?.[0] || '?').toUpperCase();
}

function Avatar({
  imageUrl,
  label,
  sizeClass = 'h-9 w-9',
}: {
  imageUrl?: string | null;
  label: string;
  sizeClass?: string;
}) {
  if (imageUrl) {
    return <img src={imageUrl} alt="" className={`${sizeClass} rounded-full object-cover`} />;
  }
  return (
    <span
      className={`${sizeClass} rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center`}
      aria-hidden
    >
      {label}
    </span>
  );
}

export default function UserMenu({
  variant = 'dropdown',
  onNavigate,
}: {
  variant?: 'dropdown' | 'stack';
  onNavigate?: () => void;
}) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const email = user?.primaryEmailAddress?.emailAddress || null;
  const prefix = readProfileExtras(user?.unsafeMetadata).namePrefix;
  const name = displayName(prefix, user?.firstName, user?.lastName, email);
  const avatarLabel = initials(user?.firstName, user?.lastName, email);

  useEffect(() => {
    if (!open) return;

    function handlePointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  async function handleSignOut() {
    onNavigate?.();
    setOpen(false);
    await signOut({ redirectUrl: '/' });
  }

  const links = (
    <>
      <Link
        to="/profile"
        className="flex items-center gap-2 min-h-[44px] px-3 text-sm text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md focus-ring"
        onClick={() => {
          onNavigate?.();
          setOpen(false);
        }}
      >
        <UserCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
        Profile
      </Link>
      <button
        type="button"
        className="flex items-center gap-2 w-full min-h-[44px] px-3 text-sm text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md focus-ring"
        onClick={handleSignOut}
      >
        <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
        Sign out
      </button>
    </>
  );

  if (variant === 'stack') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-3 px-1 pb-3 mb-2 border-b border-gray-200">
          <Avatar imageUrl={user?.imageUrl} label={avatarLabel} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
            {email && <p className="text-xs text-gray-500 truncate">{email}</p>}
          </div>
        </div>
        {links}
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="flex items-center gap-2 min-h-[44px] min-w-[44px] px-1 rounded-md hover:bg-gray-50 focus-ring"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((current) => !current)}
      >
        <Avatar imageUrl={user?.imageUrl} label={avatarLabel} />
        <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50"
        >
          <div className="px-3 pb-2 mb-1 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
            {email && <p className="text-xs text-gray-500 truncate">{email}</p>}
          </div>
          {links}
        </div>
      )}
    </div>
  );
}
